import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { lettersApi } from '../features/letters/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';
import { displayName } from '../lib/utils.ts';
import { formatDvDateShort } from '../lib/dvDate.ts';

export function Dashboard(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const { data: letters = [], isLoading, isError } = useQuery({ queryKey: ['letters'], queryFn: lettersApi.list });
  const counts = [
    { label: dv.letters.title, icon: 'description', value: letters.length },
    { label: dv.letters.draftPill, icon: 'edit_note', value: letters.filter((letter) => letter.status === 'DRAFT').length },
    { label: dv.letters.finalizedPill, icon: 'task', value: letters.filter((letter) => letter.status === 'FINALIZED').length },
  ];

  return (
    <div className="page space-y-8 lg:space-y-10">
      <header className="flex flex-wrap items-center justify-between gap-5">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">
            {dv.dashboard.welcome}{user ? `، ${displayName(user)}` : ''}
          </h1>
          {user && (
            <p className="text-sm text-muted-foreground">
              {user.designationDv ?? dv.roles[user.role]}
            </p>
          )}
        </div>
        <Link to="/letters/new" className="btn-primary">
          <Icon name="edit_square" size={20} />
          {dv.letters.write}
        </Link>
      </header>

      <section aria-label={dv.letters.title} className="grid gap-4 sm:grid-cols-3">
        {counts.map((item) => (
          <div key={item.icon} className="card-paper flex items-center gap-4 p-5 sm:p-6">
            <Icon name={item.icon} size={28} className="shrink-0 text-primary" />
            <div className="min-w-0 space-y-1">
              <p className="text-sm text-muted-foreground">{item.label}</p>
              <p className="tabular text-3xl font-semibold" dir="ltr">{isLoading || isError ? '—' : item.value}</p>
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold">{dv.letters.title}</h2>
          <Link to="/letters" className="btn-ghost btn-sm">
            {dv.letters.open}<Icon name="arrow_back" size={18} />
          </Link>
        </div>
        <div className="card-paper divide-y divide-border overflow-hidden p-0">
          {isError ? (
            <p role="alert" className="p-8 text-center text-sm text-destructive">{dv.common.error}</p>
          ) : letters.length === 0 ? (
            <div className="flex flex-col items-center gap-4 px-6 py-14 text-center">
              <Icon name="description" size={40} className="text-muted-foreground" />
              <p role="status" className="text-sm text-muted-foreground">{isLoading ? dv.common.loading : dv.letters.empty}</p>
            </div>
          ) : letters.slice(0, 5).map((letter) => (
            <Link key={letter.id} to="/letters/$id" params={{ id: letter.id }} className="flex items-center gap-4 px-4 py-4 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5">
              <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                <Icon name="description" size={22} />
              </span>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="truncate font-medium">{letter.subject || dv.letters.newTitle}</p>
                <p className="text-xs text-muted-foreground">{formatDvDateShort(letter.letterDate)}</p>
              </div>
              <span className={`pill shrink-0 ${letter.status === 'FINALIZED' ? 'pill-success' : 'pill-neutral'}`}>
                {letter.status === 'FINALIZED' ? dv.letters.finalizedPill : dv.letters.draftPill}
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
